export const EMAIL_STATUS = {
  PENDING: "pending",
  READY: "ready",
  SENDING: "sending",
  SENT: "sent",
  FAILED: "failed",
  CANCELLED: "cancelled",
};

// Accessors for job.status.domains.email (AS-849)
export const getEmailStatus = (job) =>
  job?.status?.domains?.email || null;
export const getEmailAlert = (job) =>
  job?.status?.domains?.email?.alert || null;
export const getEmailCounts = (job) =>
  job?.status?.domains?.email?.counts || null;
export const getEmailActionable = (job) =>
  job?.status?.domains?.email?.actionable || [];
export const getPrimaryAlert = (job) =>
  job?.status?.alerts?.[0] || null;

const DATE_FORMAT_OPTIONS = {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * Formats an email timestamp as e.g. "Oct 25, 09:30 AM".
 */
export const formatEmailTimestamp = (value) =>
  new Date(value).toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);

const STATUS_CONFIG = {
  [EMAIL_STATUS.FAILED]: { color: "#DC3545", label: "Failed" },
  [EMAIL_STATUS.READY]: { color: "#0D6EFD", label: "Ready to Send" },
  [EMAIL_STATUS.SENDING]: { color: "#0DCAF0", label: "Sending" },
  [EMAIL_STATUS.SENT]: { color: "#198754", label: "Sent" },
  [EMAIL_STATUS.PENDING]: { color: "#6C757D", label: "Pending" },
  [EMAIL_STATUS.CANCELLED]: { color: "#ADB5BD", label: "Cancelled" },
};

// Summary keys ordered by display priority (Failed first)
const SUMMARY_KEYS_BY_PRIORITY = [
  { key: "failed", status: EMAIL_STATUS.FAILED },
  { key: "ready", status: EMAIL_STATUS.READY },
  { key: "sending", status: EMAIL_STATUS.SENDING },
  { key: "sent", status: EMAIL_STATUS.SENT },
  { key: "pending", status: EMAIL_STATUS.PENDING },
  { key: "cancelled", status: EMAIL_STATUS.CANCELLED },
];

export const getStatusColor = (status) => {
  return (
    STATUS_CONFIG[status]?.color || STATUS_CONFIG[EMAIL_STATUS.PENDING].color
  );
};

export const getStatusLabel = (status) => {
  return STATUS_CONFIG[status]?.label || "Unknown";
};

/**
 * Returns the highest-priority non-zero status from a summary object.
 * Priority: Failed > Ready > Sending > Sent > Pending > Cancelled
 */
export const getPrimarySummaryStatus = (summary) => {
  if (!summary) return null;
  for (const { key, status } of SUMMARY_KEYS_BY_PRIORITY) {
    if (summary[key] > 0) return status;
  }
  return null;
};

/**
 * Formats a summary object into a display string.
 * Example: "1 Failed · 1 Sent · 2 Pending"
 * Skips zero counts and cancelled. Ordered by priority.
 */
export const getSummaryDisplay = (summary) => {
  if (!summary) return "";

  const parts = SUMMARY_KEYS_BY_PRIORITY.filter(
    ({ key }) => key !== "cancelled" && summary[key] > 0
  ).map(({ key, status }) => `${summary[key]} ${getStatusLabel(status)}`);

  return parts.join(" · ");
};

/**
 * Returns summary parts as an array of { text, color } objects
 * for rendering with per-status coloring.
 */
export const getSummaryParts = (summary) => {
  if (!summary) return [];

  return SUMMARY_KEYS_BY_PRIORITY
    .filter(({ key }) => key !== "cancelled" && summary[key] > 0)
    .map(({ key, status }) => ({
      text: `${summary[key]} ${getStatusLabel(status)}`,
      color: getStatusColor(status),
    }));
};

/**
 * Checks if an email automation summary has any non-zero counts
 * (excluding cancelled).
 */
export const hasSummaryData = (summary) => {
  if (!summary) return false;
  return SUMMARY_KEYS_BY_PRIORITY.some(
    ({ key }) => key !== "cancelled" && summary[key] > 0
  );
};

/**
 * Computes a summary counts object from an array of email log objects.
 */
export const computeSummaryFromLogs = (logs) => {
  const summary = Object.fromEntries(
    SUMMARY_KEYS_BY_PRIORITY.map(({ key }) => [key, 0])
  );
  for (const log of logs) {
    const key = log.status === EMAIL_STATUS.READY ? "ready" : log.status;
    if (key in summary) {
      summary[key] += 1;
    }
  }
  return summary;
};

/**
 * Copies text to clipboard with fallback for older browsers.
 * Returns true on success, false on failure.
 */
export const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to fallback
    }
  }

  // Fallback: textarea + execCommand
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
};
